import React, { useEffect, useState } from 'react';
import { Field } from 'formik';
import _ from 'lodash';
import { AddIcon, CheckboxField, FormGroup, Loading } from '@tradingblock/components';
import { CashieringViewFormValues, CashieringViewProps } from '../data/useCashieringView';
import { MiscUploadFileField } from '../../../../components/form/UploadMiscFile';
import { useStateSelector } from '../../../../data/global/dataSelectors';
import { useApi } from '../../../../context/Api';
import { CashieringDocumentType } from '@tradingblock/types';
import { useClearer } from '../../../../hooks/useClearer';
import { StepperInput } from '@tradingblock/components';

interface BaseType {
  id: number;
}

interface GenericAccountListProps<T extends BaseType> {
  field: string;
  data?: T[];
  selectable?: boolean;
  renderName?: (item: T) => JSX.Element;
  renderIcon?: (item: T) => JSX.Element;
  renderStatus?: (item: T) => JSX.Element | null;
  renderFootnote?: () => JSX.Element | null;
  addBtn?: { text: string; help?: string; active?: boolean };
  isDisabled?: (item: T) => boolean;
  onAdd: () => void;
  onSelect?: (id: number) => void;
  onDelete?: (item: T) => void;
}

export interface AccountListWrapperProps<T extends BaseType>
  extends GenericAccountListProps<T>,
    CashieringViewProps<CashieringViewFormValues> {
  onVerify?: (account: T) => void;
  onRename?: (account: T) => void;
}

export const createGenericAccountList = <T extends { id: number; state?: any }>() => {
  const GenericAccountList: React.FunctionComponent<GenericAccountListProps<T>> = ({
    field,
    data,
    selectable,
    addBtn,
    renderName,
    renderIcon,
    renderStatus,
    renderFootnote,
    isDisabled,
    onSelect,
    onDelete,
    onAdd,
    values,
  }) => {
    const api = useApi();
    const [showError, setShowError] = useState(false);
    const [selectedId, setSelectedId] = useState<number>();
    const isPendingStatus = (item: T) => {
      return item && item.state && item.state.includes('Pending');
    };

    // get the selected id from the values
    const selectedValuesId =
      values && values.transfer && values.transfer.mechanism === 'Ach'
        ? values.transfer.achRelationshipId
        : values.transfer.instructionId;

    useEffect(() => {
      if (selectedValuesId) {
        setSelectedId(selectedValuesId);
      }
    }, [selectedValuesId]);

    const accountId = useStateSelector(s => s.account.accountId);
    const [uploadId, setUploadId] = useState<number>();
    const clearer = useClearer();

    const handleDocumentUpload = (documentId: number) => {
      setUploadId(documentId);
    };

    const [documentType, setDocumentType] = useState<CashieringDocumentType | string>('');
    const [intermediaryDocumentType, setIntermediaryDocumentType] = useState<CashieringDocumentType | string>('');

    const isTransferInstructionField = field === 'transfer.instructionId';

    const [bankIdsWithUploads, setBankIdsWithUploads] = useState<number[]>([]);
    const [bankIdsWithMultipleUploads, setBankIdsWithMultipleUploads] = useState<number[]>([]);
    const [bankIdsWithIntermediary, setBankIdsWithIntermediary] = useState<number[]>([]);
    const [isFetchingDocuments, setIsFetchingDocuments] = useState<boolean>(true);

    const CashieringDocumentOptions = [
      { value: CashieringDocumentType.BankStatement, label: 'Bank Statement' },
      { value: CashieringDocumentType.VoidedCheck, label: 'Voided Check' },
    ];

    const handleSelect = (id: number) => {
      setShowError(false);
      if (selectable) {
        setSelectedId(id);
        if (onSelect) {
          onSelect(id);
        }
      }
    };

    useEffect(() => {
      const getDocumentIds = async (item: T) => {
        const { responseCode, payload } = await api.cashiering.documents.getId(accountId, item.id);
        if (responseCode === 0 && payload && payload.length > 0) {
          setBankIdsWithUploads(current => [...current, item.id]);
          if (payload.length > 1) {
            setBankIdsWithMultipleUploads(current => [...current, item.id]);
          }
        }
        const timer = setTimeout(() => {
          setIsFetchingDocuments(false);
        }, 500);

        return () => {
          clearTimeout(timer);
        };
      };

      const getIntermediaryIds = async (item: any) => {
        if (item.intermediary) {
          setBankIdsWithIntermediary(current => [...current, item.id]);
        }
      };

      data &&
        data.forEach(item => {
          if (isTransferInstructionField) {
            getDocumentIds(item);
            getIntermediaryIds(item);
          }
        });
    }, [data]);

    return (
      <>
        {selectable && (
          <div className="account-selection-guidance">
            <i className="fas fa-info-circle" /> Please select an account before proceeding
          </div>
        )}
        {data && (
          <ul className={`linked-accounts ${selectable ? 'checklist' : 'non-selectable'}`}>
            {_.map(data, (a, i) => (
              <li
                key={`data${i}`}
                className={`${isDisabled && isDisabled(a) ? 'disabled' : ''} ${
                  selectable && selectedId === a.id ? 'selected' : ''
                }`}
                onClick={
                  selectable && (!isDisabled || (isDisabled && !isDisabled(a))) && onSelect
                    ? () => handleSelect(a.id)
                    : undefined
                }
                style={selectable ? undefined : { cursor: 'default' }}
              >
                <div className="linked-accounts-icon">
                  {selectable &&
                    (isDisabled && isDisabled(a) ? (
                      <i className="far fa-times fa-2x neg" />
                    ) : (
                      <>
                        <Field
                          component={CheckboxField}
                          id={field}
                          type="radio"
                          value={a.id}
                          label={' '}
                          onChanged={() => handleSelect(a.id)}
                          disabled={a.state && a.state !== 'Approved'}
                          className="account-radio"
                        />
                      </>
                    ))}

                  {!selectable && renderIcon && renderIcon(a)}
                </div>
                <div className="linked-accounts-name">
                  {selectable && renderName && (
                    <>
                      <label
                        htmlFor={`${field}.${a.id}`}
                        className={`${isDisabled && isDisabled(a) ? 'disabled' : ''} ${
                          selectable && a.id === field ? 'selected' : ''
                        }`}
                        onClick={
                          selectable && (!isDisabled || (isDisabled && !isDisabled(a))) && onSelect
                            ? () => handleSelect(a.id)
                            : undefined
                        }
                        style={selectable ? undefined : { cursor: 'default' }}
                      >
                        {renderName(a)}
                      </label>
                      {isTransferInstructionField && !isFetchingDocuments && (
                        <div className="pending linked-accounts-warning txt-sm" style={{ fontStyle: 'italic' }}>
                          <strong>
                            {!bankIdsWithUploads.includes(a.id) && a.state && a.state !== 'Rejected' && (
                              <p>
                                <i className="fas fa-exclamation-circle" /> REQUIRED: Please upload bank statement or
                                voided check for bank account entered. Select document type and click "Upload Document"
                                (PDF, JPG, PNG, GIF).
                              </p>
                            )}
                            {bankIdsWithIntermediary.includes(a.id) &&
                              !bankIdsWithMultipleUploads.includes(a.id) &&
                              a.state &&
                              a.state !== 'Rejected' && (
                                <span>
                                  <i className="fas fa-exclamation-circle" /> REQUIRED: Additional upload required for
                                  intermediary bank account. Please upload bank statement or voided check for bank
                                  account entered. Select document type and click "Upload Document" (PDF, JPG, PNG,
                                  GIF).
                                </span>
                              )}
                          </strong>
                        </div>
                      )}
                    </>
                  )}
                  {!selectable && renderName && (
                    <div>
                      {renderName(a)}
                      {isTransferInstructionField && !isFetchingDocuments && (
                        <div className="pending linked-accounts-warning txt-sm" style={{ fontStyle: 'italic' }}>
                          <strong>
                            {!bankIdsWithUploads.includes(a.id) && a.state && a.state !== 'Rejected' && (
                              <p>
                                <i className="fas fa-exclamation-circle" /> REQUIRED: Please upload bank statement or
                                voided check for bank account entered. Select document type and click "Upload Document"
                                (PDF, JPG, PNG, GIF).
                              </p>
                            )}
                            {bankIdsWithIntermediary.includes(a.id) &&
                              !bankIdsWithMultipleUploads.includes(a.id) &&
                              a.state &&
                              a.state !== 'Rejected' && (
                                <span>
                                  <i className="fas fa-exclamation-circle" /> REQUIRED: Additional upload required for
                                  intermediary bank account. Please upload bank statement or voided check for bank
                                  account entered. Select document type and click "Upload Document" (PDF, JPG, PNG,
                                  GIF).
                                </span>
                              )}
                          </strong>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {renderStatus && <div className="linked-accounts-status">{renderStatus(a)}</div>}
                {!isPendingStatus(a) && <div className="linked-accounts-upload"></div>}
                {isPendingStatus(a) && (
                  <>
                    {isFetchingDocuments && isTransferInstructionField && (
                      <div className="linked-accounts-upload">
                        <Loading size="small" />
                      </div>
                    )}
                    {!isTransferInstructionField && <div className="linked-accounts-upload"></div>}
                    {isTransferInstructionField && !isFetchingDocuments && (
                      <div className="linked-accounts-upload">
                        {!bankIdsWithUploads.includes(a.id) && (
                          <>
                            <FormGroup>
                              <StepperInput
                                name={`docType-${a.id}`}
                                options={CashieringDocumentOptions}
                                value={documentType}
                                onchange={(e, value: CashieringDocumentType) => {
                                  setDocumentType(value);
                                }}
                              />
                            </FormGroup>
                            <FormGroup>
                              <Field
                                component={MiscUploadFileField}
                                id={`uploadDocuments-${i}`}
                                accountId={accountId}
                                accountHolderIndex={i}
                                description={'Document'}
                                documentType={documentType}
                                handleDocumentUpload={handleDocumentUpload}
                                isCashieringDocument={true}
                                recipientBankId={a.id}
                                disabled={documentType === ''}
                                clearer={clearer}
                              />
                            </FormGroup>
                          </>
                        )}
                        {bankIdsWithIntermediary.includes(a.id) && !bankIdsWithMultipleUploads.includes(a.id) && (
                          <>
                            <FormGroup>
                              <StepperInput
                                name={`docType-${a.id}-intermediary`}
                                options={CashieringDocumentOptions}
                                value={intermediaryDocumentType}
                                onchange={(e, value: CashieringDocumentType) => {
                                  setIntermediaryDocumentType(value);
                                }}
                              />
                            </FormGroup>
                            <Field
                              component={MiscUploadFileField}
                              id={`uploadDocuments-${i}-intermediary`}
                              accountId={accountId}
                              accountHolderIndex={i}
                              description={'Document'}
                              documentType={intermediaryDocumentType}
                              handleDocumentUpload={handleDocumentUpload}
                              isCashieringDocument={true}
                              recipientBankId={a.id}
                              disabled={intermediaryDocumentType === ''}
                              clearer={clearer}
                            />
                          </>
                        )}
                      </div>
                    )}
                  </>
                )}
                {!selectable && onDelete && (!isDisabled || !isDisabled(a)) && (
                  <div className="linked-accounts-remove">
                    <button className="btn btn-blend" type="button" title="Delete" onClick={() => onDelete(a)}>
                      <i className="fas fa-2x fa-times-circle" />
                    </button>
                  </div>
                )}

                {selectedId === a.id && <i className="fal fa-check-square fa-2x" style={{ color: 'var(--teal)' }}></i>}
              </li>
            ))}
          </ul>
        )}

        {renderFootnote && renderFootnote()}

        {addBtn && (
          <p>
            <span
              onClick={onAdd}
              onKeyDown={e => (e.keyCode === 13 || e.keyCode === 32) && onAdd && onAdd()}
              style={{ cursor: 'pointer' }}
              role="button"
              tabIndex={0}
            >
              <AddIcon size="1x" inverse active={addBtn.active} />
            </span>
            <button className="btn btn-link" type="button" onClick={onAdd} style={{ paddingLeft: '1em' }}>
              {addBtn.text}
            </button>{' '}
            {addBtn.help && <span className="mute" dangerouslySetInnerHTML={{ __html: addBtn.help }} />}
          </p>
        )}

        {showError && (
          <div className="account-selection-error">
            <i className="fas fa-exclamation-circle" /> You must select a bank account to proceed
          </div>
        )}
      </>
    );
  };

  return GenericAccountList;
};
